// Package imports:
import React from 'react';
// Component imports:
import ColoredAccordion from '../../ui-elements/ColoredAccordion/ColoredAccordion';
import ExampleReportButton from '../ExampleReportButton/ExampleReportButton';

interface InstructionsProps {
    isIndividual: boolean;
    noAccess?: boolean;
}

// Define the step data structure for better type safety and maintainability
interface AccordionStep {
    id: string;
    title: string;
    content: string;
}

// Configuration object that holds all step definitions
// This separates data from presentation logic, making translations and updates easier
const ACCORDION_STEPS: Record<'company' | 'individual', AccordionStep[]> = {
    company: [
        {
            id: 'step-1',
            title: 'Hefja könnun',
            content: 'Veldu þá viðskiptavini sem hefja skal könnun á. Helstu gögn verða sótt og uppflettingar framkvæmdar á tengilið, prókúruhöfum og raunverulegum eigendum, meðal annars í PEP og öðrum válistum.'
        },
        {
            id: 'step-2',
            title: 'Skrá tengilið',
            content: 'Skráðu tengilið lögaðila ásamt netfangi. Skráður tengiliður þarf að hafa heimild til að koma fram fyrir hönd lögaðila. Þá er valkvætt að senda beiðni á aðra prókúruhafa sem skrá sig inn með rafrænum skilríkjum og sanna á sér deili.'
        },
        {
            id: 'step-3',
            title: 'Senda könnun',
            content: 'Sendu könnun á skráðan tengilið lögaðila. Tölvupóstur er sendur út í nafni tilkynningarskylds aðila með hlekk á könnun. Tengiliður skráir sig inn með rafrænum skilríkjum og svarar spurningalista. Þessa aðgerð má endurtaka og virkar hún þá sem áminning eða ítrekun.'
        },
        {
            id: 'step-4',
            title: 'Áhættumat skráð',
            content: 'Skráðu áhættumat handvirkt eða sjálfvirkt með litakóða ásamt athugasemdum þegar öll gögn liggja fyrir, fyrir hvern viðskiptavin eða marga í einu. Hægt er að hlaða upp allt að 10 skjölum við niðurstöðu könnunar.'
        }
    ],
    individual: [
        {
            id: 'step-1',
            title: 'Hefja könnun',
            content: 'Veldu þá viðskiptavini sem hefja skal könnun á. Helstu gögn verða sótt og uppflettingar framkvæmdar á einstaklingnum, meðal annars í PEP og öðrum válistum.'
        },
        {
            id: 'step-2',
            title: 'Senda könnun',
            content: 'Sendu könnun á einn eða fleiri einstaklinga í einu. Tölvupóstur er sendur út í nafni tilkynningarskylds aðila með hlekk á könnun. Einstaklingar skrá sig inn með rafrænum skilríkjum og svara spurningalista. Þessa aðgerð má endurtaka og virkar hún þá sem áminning eða ítrekun.'
        },
        {
            id: 'step-3',
            title: 'Áhættumat skráð',
            content: 'Skráðu áhættumat handvirkt eða sjálfvirkt með litakóða ásamt athugasemdum þegar öll gögn liggja fyrir, fyrir hvern viðskiptavin eða marga í einu. Hægt er að hlaða upp allt að 10 skjölum við niðurstöðu könnunar.'
        }
    ]
};

// Helper function to transform step data into ColoredAccordion format
// This maps our clean data structure to the format expected by the ColoredAccordion component
const formatStepsForAccordion = (steps: AccordionStep[]) => {
    return steps.map(step => ({
        id: step.id,
        title: <p>{step.title}</p>,
        content: <p className='paragraph-small'>{step.content}</p>
    }));
};

const Instructions: React.FC<InstructionsProps> = ({
    isIndividual,
    noAccess
}) => {
    // Determine which accordion type to show based on isIndividual prop
    const accordionType = isIndividual ? 'individual' : 'company';

    // Map accordion type to corresponding color (company = blue, individual = green)
    const accordionColor = isIndividual ? 'green' : 'blue';

    return (
        <div className="KCL_instructions">
            <div className="left">
                <div className="text-area">
                    <span className='summary-paragraph'>
                        KYC kerfið („Know your customer") er ætlað til utanumhalds og framkvæmdar áreiðanleikakannana og stenst þau lög sem tilkynningarskyldir aðilar falla undir um aðgerðir gegn peningaþvætti (AML) og fjármögnun hryðjuverka (CTF).
                    </span>
                    <div className="instruction-text">
                        <p className='paragraph'>
                            Kerfið gerir notendum kleift að framkvæma áreiðanleikakannanir á einfaldan hátt og öðlast góða yfirsýn á viðskiptavinum, lögaðilum og einstaklingum. Allar aðgerðir eru skráðar og aðgengilegar gagnvart eftirlitsaðilum.
                        </p>
                        {!noAccess && <>
                            <p className='paragraph'>
                                Áminningar eru sendar sjálfvirkt úr kerfinu þegar hámarks tími er liðinn frá síðustu könnun. <br />
                            </p>
                            <p className='paragraph'>
                                Athugið áhættumeiri viðskiptavinir skulu sæta tíðari uppfærslu áreiðanleikakönnunar.
                            </p>
                        </>
                        }
                    </div>
                    {!noAccess &&
                        <ExampleReportButton
                            reportType={isIndividual ? "kyc-individual" : "kyc-company"}
                            size="lg"
                        />
                    }
                </div>
            </div>

            {/* Don't show instructions if noAccess */}
            <div className="right">
                {!noAccess &&
                    <div className="instruction-steps">
                        <h6>Leiðbeiningar fyrir {isIndividual ? 'einstaklinga' : 'lögaðila'}</h6>

                        <ColoredAccordion
                            items={formatStepsForAccordion(ACCORDION_STEPS[accordionType])}
                            openId='step-1'
                            color={accordionColor}
                        />
                    </div>
                }
            </div>
        </div>
    );
}

export default Instructions;